import { API, post } from './api';
import { CAMPAIGN_ASSET_IMAGE, CAMPAIGN_ASSET_PDF, CAMPAIGN_ASSET_VIDEO } from './campaignAsset';
import { logError } from './log';
import { uploadCloudinaryVideo } from 'utils/cloudinary-video-upload';
import { uploadCollabaryVideo } from 'utils/collabary-video-upload';

import type { CampaignAsset, NewCampaignAsset, UploadFileResponse } from 'types';

type UploadFileResult =
  | { type: 'success', data: UploadFileResponse, file: File, signature: string }
  | { type: 'error', error: Error, file: File }

const { IMAGES, UPLOAD } = API.END_POINTS.API;

export function uploadImage(image) {
  const url = IMAGES;
  const data = new FormData();
  const file = image.file || image;

  data.append('file', file);

  return post(url, { data })
    .then((response) => ({
      meta: {
        size: file.size
      },
      fileName: file.name,
      assetType: file.type.includes('image') ? CAMPAIGN_ASSET_IMAGE : CAMPAIGN_ASSET_PDF,
      url: response.data.url
    }));
}

// upload to Cloudinary
function uploadVideo(video, withCollabaryUpload: boolean) {
  const file = video.file || video;

  if (withCollabaryUpload) {
    return uploadCollabaryVideo(video)
      .then((response) => ({
        meta: {
          size: video.size,
          videoUploadResponse: response
        },
        assetType: CAMPAIGN_ASSET_VIDEO,
        url: response.data.url,
        fileName: file.name
      }));
  }

  return uploadCloudinaryVideo(video)
    .then((response) => ({
      meta: {
        duration: response.duration,
        size: video.size,
        cloudinary: {
          publicId: response.publicId
        }
      },
      assetType: CAMPAIGN_ASSET_VIDEO,
      url: response.secureUrl,
      fileName: file.name
    }));
}

export function uploadImages(images) {
  return Promise.all(images.map(uploadImage))
    .then((uploadedImages) => uploadedImages)
    .catch((error) => {
      throw error;
    });
}

export function uploadVideos(videos, withCollabaryUpload: boolean) {
  return Promise.all(videos.map(video => uploadVideo(video, withCollabaryUpload)))
    .then((uploadedVideos) => uploadedVideos)
    .catch((error) => {
      throw error;
    });
}

export function addTempIds(assets: Array<CampaignAsset | NewCampaignAsset>): Array<CampaignAsset> {
  const datetime = (new Date()).getTime(); // `datetime` and `index` are helpful for the correct sorting
  return assets.map((a, index) => ({
    ...a,
    isNew: true, // indicator, that this image is new and `id` is temporary
    // FE uses a temp id before we save a brief and get real `id` from API
    id: `temp-${datetime}-${index}-${a.assetType}`
  }));
}

export const uploadFile = async (
  file: File,
  url = UPLOAD
): Promise<UploadFileResult> => {
  try {
    const response = await post(url, {
      data: file,
      headers: {
        'Content-Type': file.type
      }
    });
    const data = response.data;

    if (data && data.data) {
      data.data.name = file.name;
    }

    return {
      type: 'success',
      data,
      file,
      signature: data.signature || ''
    };
  } catch (error) {
    logError(error);

    return {
      type: 'error',
      error,
      file
    };
  }
};
